// Imports

import { formatNumber, parseDate } from "@/lib/utils";
import i18n from "@/i18n";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";
import {
  docTypeOptions,
  extractTypeOptions,
  frequencyOptions,
  statusOptions,
} from "./filter";
import { getUnzipStrategyLabel } from "./unzipStrategy";

// Helper functions to map enum values to labels
const getExtractTypeLabel = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "";
  const option = extractTypeOptions.find(
    (opt) => opt.value === value.toString(),
  );
  return option ? option.label : value.toString();
};

// const getTransformTypeLabel = (value: number | null | undefined): string => {
//   if (value === null || value === undefined) return "";
//   const option = transformTypeOptions.find(
//     (opt) => opt.value === value.toString(),
//   );
//   return option ? option.label : value.toString();
// };

// const getLoadTypeLabel = (value: number | null | undefined): string => {
//   if (value === null || value === undefined) return "";
//   const option = loadTypeOptions.find((opt) => opt.value === value.toString());
//   return option ? option.label : value.toString();
// };

const getStatusLabel = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "";
  const option = statusOptions.find((opt) => opt.value === value.toString());
  return option ? option.label : value.toString();
};

const getFrequencyLabel = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "";
  const option = frequencyOptions.find((opt) => opt.value === value.toString());
  return option ? option.label : value.toString();
};

const getDocTypeLabel = (value: string | null | undefined): string => {
  if (value === null || value === undefined) return "";
  const option = docTypeOptions.find((opt) => opt.value === value);
  return option ? option.label : value;
};

// Type of the fields in the table

export type ProviderConfig = {
  id: number;
  companies?: { id: number; name: string }[];
  companies_names?: string; // In case companies are pre-joined as string
  client?: string | null;
  file_pattern?: string | null;
  code?: string | null;
  title?: string | null;
  source_folder?: string;
  unzip_strategy?: string | null;
  connector_config_id?: number | null;
  connector_config?: string | null;
  extract_type?: number | null;
  transform_type?: number | null;
  load_type?: number | null;
  file_type?: number | null;
  status?: number | null;
  frequency?: number | null;
  doc_type?: string | null;
  priority?: number | null;
  last_computed_date?: string | null;
  last_error_date?: string | null;
  is_client_config?: boolean;
  py_type?: string | null;
  py_extract?: string | null;
  py_transform?: string | null;
  py_load?: string | null;
  py_table?: string | null;
  py_preprocess_parameter_store_gpg_passphrase?: string | null;
  py_preprocess_parameter_store_gpg_private?: string | null;
  py_preprocess_zip_password?: string | null;
  py_extract_encoding?: string | null;
  py_extract_header?: number | null;
  py_extract_engine?: string | null;
  py_extract_skipfooter?: number | null;
  py_extract_concat_all_sheets?: boolean;
  py_transform_reporting_date?: string | null;
  py_transform_strict_mapping?: boolean;
};

// Definition of the columns

export const provider_configs_columns: ColumnDef<ProviderConfig>[] = [
  {
    accessorKey: "id",
    header: "ID",
  },
  {
    accessorKey: "companies_names",
    header: "Companies",
    cell: ({ row }) => {
      // Prefer companies_names if present, else companies array
      const companiesNames =
        row.original.companies_names ||
        (Array.isArray(row.original.companies)
          ? row.original.companies
              .map((c) => c.name)
              .sort((a, b) => a.localeCompare(b))
              .join(", ")
          : "");
      return <span>{companiesNames}</span>;
    },
  },
  {
    accessorKey: "client",
    header: "Client",
  },
  {
    accessorKey: "file_pattern",
    header: "File Pattern",
  },
  {
    accessorKey: "code",
    header: "Code",
  },
  {
    accessorKey: "connector_config",
    header: "Connector Config",
  },
  {
    accessorKey: "title",
    header: "Title",
  },
  {
    accessorKey: "unzip_strategy",
    header: "Unzip Strategy",
    cell: ({ row }) => {
      return getUnzipStrategyLabel(row.getValue("unzip_strategy"));
    },
  },
  {
    accessorKey: "file_type",
    header: "File Type",
    cell: ({ row }) => {
      return getExtractTypeLabel(row.getValue("file_type"));
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      return getStatusLabel(row.getValue("status"));
    },
  },
  {
    accessorKey: "frequency",
    header: "Frequency",
    cell: ({ row }) => {
      return getFrequencyLabel(row.getValue("frequency"));
    },
  },
  {
    accessorKey: "doc_type",
    header: "Document Type",
    cell: ({ row }) => {
      return getDocTypeLabel(row.getValue("doc_type"));
    },
  },
  {
    accessorKey: "priority",
    header: () => <div className="text-right">Priority</div>,
    cell: ({ row }) => (
      <div className="text-right">
        {formatNumber(row.getValue("priority"), 0, 0)}
      </div>
    ),
  },
  {
    accessorKey: "last_computed_date",
    header: "Last Computed Date",
    cell: ({ row }) => parseDate(row.getValue("last_computed_date")),
  },
  {
    accessorKey: "last_error_date",
    header: "Last Error Date",
    cell: ({ row }) => parseDate(row.getValue("last_error_date")),
  },
  {
    accessorKey: "is_client_config",
    header: "Client Config",
    cell: ({ row }) => {
      const val = row.getValue("is_client_config");
      return <span>{val === true ? "Yes" : val === false ? "No" : ""}</span>;
    },
  },
  {
    id: "actions",
    header: () => <div className="text-right">Actions</div>,
    cell: ({ row }) => {
      const id = row.getValue("id");
      const basePath =
        typeof window !== "undefined" ? window.location.pathname : "";

      return (
        <div className="flex justify-end gap-2">
          <Link
            href={`${basePath}/${id}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      );
    },
  },
];
